import { useState, useEffect, useMemo } from "react";
import { Phone, RefreshCw, ChevronRight } from "lucide-react";
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Statuts disponibles pour l'appel qualité
const STATUTS_APPEL_QUALITE = [
  { value: '', label: '-', color: 'bg-slate-700 text-slate-300' },
  { value: 'OK POUR CONTINUER ÉTAPE AMO', label: 'OK POUR CONTINUER ÉTAPE AMO', color: 'bg-green-600 text-white' },
  { value: 'EN RÉFLEXION - AUTRES RAISONS', label: 'EN RÉFLEXION - AUTRES RAISONS', color: 'bg-orange-300 text-slate-900' },
  { value: 'EN RÉFLEXION - NÉGOCIER LE RAC', label: 'EN RÉFLEXION - NÉGOCIER LE RAC', color: 'bg-amber-600 text-white' },
  { value: 'R1 PAS FAIT - RDV REPOSITIONNÉ', label: 'R1 PAS FAIT - RDV REPOSITIONNÉ', color: 'bg-blue-400 text-slate-900' },
  { value: 'R1 PAS FAIT - RDV À REPOSITIONNER', label: 'R1 PAS FAIT - RDV À REPOSITIONNER', color: 'bg-yellow-400 text-slate-900' },
  { value: 'PAS SIGNÉ - NE VEUT PAS DONNER SUITE', label: 'PAS SIGNÉ - NE VEUT PAS DONNER SUITE', color: 'bg-red-600 text-white' },
  { value: 'PAS SIGNÉ - MAUVAIS TECHNICIEN', label: 'PAS SIGNÉ - MAUVAIS TECHNICIEN', color: 'bg-red-600 text-white' },
  { value: 'PAS SIGNÉ - VEUT DU 1€', label: 'PAS SIGNÉ - VEUT DU 1€', color: 'bg-red-700 text-white' },
  { value: 'PAS SIGNÉ - NE VEUT PAS PAYER AMO', label: 'PAS SIGNÉ - NE VEUT PAS PAYER AMO', color: 'bg-red-600 text-white' },
  { value: 'PAS SIGNÉ - PENSE ARNAQUE', label: 'PAS SIGNÉ - PENSE ARNAQUE', color: 'bg-red-600 text-white' },
  { value: 'PAS SIGNÉ AUTRES RAISONS', label: 'PAS SIGNÉ AUTRES RAISONS', color: 'bg-red-600 text-white' },
];

/**
 * Bannière d'alerte pour les appels qualité à effectuer
 * - Si 1 seul client : affichage direct
 * - Si plusieurs clients : système repliable (fermé par défaut)
 */
export function UrgentAppelQualiteBanner({ clients, onClientUpdate, defaultExpanded = false }) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [updatingRow, setUpdatingRow] = useState(null);
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const getBusinessDaysDiff = (startDate, endDate) => {
    let count = 0;
    let current = new Date(startDate);
    while (current < endDate) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
      current.setDate(current.getDate() + 1);
    }
    return count;
  };

  const urgentAppelsQualite = useMemo(() => {
    if (!clients) return [];
    
    const now = currentTime;
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const urgent = [];
    
    clients.forEach(client => {
      const dateR1 = client.dateR1;
      if (!dateR1) return;
      
      if (client.resultatAppel && client.resultatAppel.trim()) return;
      
      if (client.dateTransfertAmo || client.vtAmo || client.dateR2 || client.dateDepotAnah || client.dateOctroi) {
        return;
      }
      
      const parts = dateR1.split('/');
      if (parts.length !== 3) return;
      
      try {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        const r1Date = new Date(year, month - 1, day);
        
        if (r1Date >= today) return;
        
        const businessDays = getBusinessDaysDiff(r1Date, today);
        
        let urgencyLevel = 'low';
        let urgencyColor = 'bg-orange-500';
        let urgencyText = 'Appel qualité à faire';
        
        if (businessDays <= 1) {
          urgencyLevel = 'low';
          urgencyColor = 'bg-orange-400';
          urgencyText = `< 24h ouvrées depuis R1`;
        } else if (businessDays <= 2) {
          urgencyLevel = 'medium';
          urgencyColor = 'bg-orange-600';
          urgencyText = `24-48h ouvrées depuis R1`;
        } else {
          urgencyLevel = 'high';
          urgencyColor = 'bg-red-500';
          urgencyText = `> 48h ouvrées depuis R1 (${businessDays} jours)`;
        }
        
        urgent.push({
          ...client,
          r1Date: dateR1,
          r1DateObj: r1Date,
          businessDays,
          urgencyLevel,
          urgencyColor,
          urgencyText
        });
      } catch (e) {
        // Ignorer les erreurs de parsing
      }
    });
    
    // Tri du plus récent au plus ancien
    return urgent.sort((a, b) => b.r1DateObj - a.r1DateObj);
  }, [clients, currentTime]);

  const handleResultatChange = async (client, value) => {
    setUpdatingRow(client.rowNumber);
    try {
      const response = await authFetch(`${API}/api/telepro/update-appel-qualite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          row_number: client.rowNumber,
          nouveau_resultat: value
        })
      });
      
      if (response.ok) {
        if (onClientUpdate) {
          onClientUpdate();
        }
      }
    } catch (error) {
      console.error('Error updating appel qualité:', error);
    } finally {
      setUpdatingRow(null);
    }
  };

  if (urgentAppelsQualite.length === 0) return null;

  const dayNames = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
  
  const countHigh = urgentAppelsQualite.filter(c => c.urgencyLevel === 'high').length;
  const countMedium = urgentAppelsQualite.filter(c => c.urgencyLevel === 'medium').length;

  const hasMultiple = urgentAppelsQualite.length > 1;
  const showList = !hasMultiple || expanded;

  const renderClientItem = (client, idx) => (
    <div 
      key={idx}
      data-testid={`appel-qualite-client-${idx}`}
      className="bg-slate-900/50 rounded-lg p-3"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-3 h-3 rounded-full flex-shrink-0 ${client.urgencyColor} ${client.urgencyLevel === 'high' ? 'animate-pulse' : ''}`} />
          <div className="min-w-0">
            <p className="text-white font-medium truncate">
              {client.nom} {client.prenom}
              {client.telephone ? (
                <span className="ml-2 text-blue-400 font-normal">📞 {client.telephone}</span>
              ) : (
                <span className="ml-2 text-red-400 text-xs font-normal">⚠️ Tél. manquant</span>
              )}
            </p>
            <p className="text-xs text-slate-400 truncate">
              Télépro: {client.telepro || 'Non assigné'}
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-white font-medium text-sm">
            R1 : {client.r1Date} ({dayNames[client.r1DateObj.getDay()]})
          </p>
          <p className={`text-xs ${client.urgencyLevel === 'high' ? 'text-red-400 font-bold' : client.urgencyLevel === 'medium' ? 'text-orange-400' : 'text-yellow-400'}`}>
            {client.urgencyText}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-2 mt-2">
        <span className="text-xs text-slate-400">Résultat :</span>
        {updatingRow === client.rowNumber ? (
          <div className="flex-1 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
            <RefreshCw className="w-4 h-4 text-slate-400 animate-spin" />
          </div>
        ) : (
          <select
            data-testid={`appel-qualite-select-${idx}`}
            value={client.resultatAppel || ''}
            onChange={(e) => handleResultatChange(client, e.target.value)}
            className="flex-1 px-2 py-1.5 rounded-lg text-xs font-medium cursor-pointer bg-slate-700 text-slate-300 border border-slate-600 hover:border-orange-500 transition-colors"
          >
            {STATUTS_APPEL_QUALITE.map((statut) => (
              <option 
                key={statut.value} 
                value={statut.value}
                className={statut.color}
              >
                {statut.label}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="urgent-appel-qualite-banner" className="bg-gradient-to-r from-orange-900/50 to-amber-900/50 border border-orange-500/50 rounded-xl p-4 mb-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <Phone className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div 
            className={`flex items-center justify-between ${hasMultiple ? 'cursor-pointer' : ''}`}
            onClick={() => hasMultiple && setExpanded(!expanded)}
          >
            <div>
              <h3 className="font-bold text-orange-300 flex items-center gap-2 flex-wrap">
                📞 Appels Qualité à faire
                <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">
                  {urgentAppelsQualite.length} en attente
                </span>
                {countHigh > 0 && (
                  <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
                    {countHigh} &gt;48h 🔴
                  </span>
                )}
                {countMedium > 0 && (
                  <span className="text-xs bg-orange-600 text-white px-2 py-0.5 rounded-full">
                    {countMedium} 24-48h 🟠
                  </span>
                )}
              </h3>
              <p className="text-xs text-orange-200/70 mt-0.5">
                Appel qualité idéalement sous 48h ouvrées après le R1
              </p>
            </div>
            {hasMultiple && (
              <ChevronRight className={`w-5 h-5 text-orange-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            )}
          </div>
          
          {showList && (
            <div className="space-y-2 mt-3 max-h-[300px] overflow-auto">
              {urgentAppelsQualite.map((client, idx) => renderClientItem(client, idx))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UrgentAppelQualiteBanner;
